import { useState, useEffect, useContext, useRef } from "react";
import {
  Row,
  Col,
  Tabs,
  Grid,
  Button,
  Dropdown,
  Menu,
  Tooltip,
} from "antd";
import {
  CreditCardOutlined,
  EditOutlined,
  FilePdfOutlined,
  SelectOutlined,
  ExceptionOutlined,
  FileDoneOutlined,
  SendOutlined,
  MoreOutlined,
  DollarOutlined,
  StopOutlined,
} from "@ant-design/icons";

import DataTable from "../DataTable";
import CopyableCell from "../CopyableCell";

import { AuthContext } from "../../context/AuthContext";
import { TourContext } from "../../context/TourContext";
import {
  clampPage,
  getStoredActiveTab,
  getStoredTabPage,
  resolveActiveTab,
  resolveTabPage,
  setStoredActiveTab,
  setStoredTabPage,
  visibleStatusTabs,
} from "../../utils/tablePaginationStorage";
import { isValidSACellphone } from "../../utils/systemValidation";

const ACTIVATE_CELLPHONE_TOOLTIP =
  "Invalid cellphone — edit the member and correct the number before activating";

const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

const dummyData = [
  {
    id: 1,
    IdNumber: "123456789",
    Policy: { PolicyNumber: "PN12345" },
    Name: "John",
    Surname: "Doe",
    Cellphone: "1234567890",
    Email: "john.doe@example.com",
    InceptionDate: "2024-01-01",
    Group: { GroupName: "Dummy Group" },
    StatusID: 1,
  },
];

interface IMemberProps {
  members: any;
  showPaymentModal: Function;
  showDocumentModal: Function;
  showSendModal: Function;
  showEditModal: Function;
  showLapseModal: Function;
  showCancelModal: Function;
  showPremiumModal: Function;
  showActivateModal: Function;
  handleDelete: Function;
  handleManage: Function;
  handleSend: Function;
  handleActivate: Function;
  filtered: any;
  searchActive: boolean;
  storagePrefix?: string;
}

const MemberTable: React.FC<IMemberProps> = ({
  members,
  showPaymentModal,
  showDocumentModal,
  showSendModal,
  showEditModal,
  showLapseModal,
  showCancelModal,
  showPremiumModal,
  showActivateModal,
  handleManage,
  handleSend,
  handleActivate,
  filtered,
  searchActive,
  storagePrefix = "members",
}) => {
  const { currentGroup } = useContext(AuthContext);
  const {
    refManageMember,
    refEditMember,
    refPaymentMethod,
    refGenerateDocument,
    refSendDocument,
    refLapsePolicy,
    refLoadPremium,
    refActivatePolicy,
    markPageAsVisited,
    pages,
  } = useContext(TourContext);

  const initialTab = getStoredActiveTab(storagePrefix);
  const searchRestoreRef = useRef(true);
  const restoreWaitRef = useRef(false);
  const didSessionRestoreRef = useRef(false);

  const [active, setActive] = useState<any>([]);
  const [inactive, setInactive] = useState<any>([]);
  const [lapsed, setLapsed] = useState<any>([]);
  const [filteredActive, setFilteredActive] = useState<any>([]);
  const [filteredInActive, setFilteredInActive] = useState<any>([]);
  const [filteredLapsed, setFilteredLapsed] = useState<any>([]);
  const [state, setState] = useState({
    loading: true,
    activeKey: initialTab,
    pagination: {
      pageSize: 12,
      position: ["bottomRight"],
      total: 0,
      current: getStoredTabPage(storagePrefix, initialTab),
      hideOnSinglePage: true,
    },
    searchItem: "",
  });

  useBreakpoint();

  const getStatusId = (item: any) => {
    const raw =
      item?.StatusID ??
      item?.StatusId ??
      item?.Status?.id ??
      item?.Status?.StatusID;
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
  };
  const isLapsed = (item: any) =>
    item?.PolicyLapsed === true ||
    item?.PolicyLapsed === 1 ||
    String(item?.PolicyLapsed).toLowerCase() === "true";
  const isInactiveMember = (item: any) =>
    getStatusId(item) === 2 && !isLapsed(item);
  const isActiveMember = (item: any) =>
    !isLapsed(item) && !isInactiveMember(item);

  const applyTabTotal = (tabKey: string, total: number) => {
    setState((prev) => {
      if (prev.activeKey !== tabKey) {
        return { ...prev, loading: false };
      }
      const pageSize = prev.pagination.pageSize || 12;
      const current = resolveTabPage(
        storagePrefix,
        tabKey,
        prev.pagination.current || 1,
        total,
        pageSize
      );
      return {
        ...prev,
        loading: false,
        pagination: {
          ...prev.pagination,
          total,
          current,
        },
      };
    });
  };

  const fetchActive = async () => {
    try {
      const list = Array.isArray(members) ? members : [];
      if (list.length > 0) {
        const activeMembers = list.filter(isActiveMember);

        let activeFiltered: any[] = [];
        if (filtered.length > 0) {
          activeFiltered = filtered.filter(isActiveMember);
          setFilteredActive(activeFiltered);
        } else {
          setFilteredActive([]);
        }

        const total = searchActive
          ? activeFiltered.length
          : activeMembers.length;

        applyTabTotal("1", total);
        setActive(activeMembers);
      } else {
        setActive(dummyData);
        applyTabTotal("1", 0);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchInActive = async () => {
    try {
      const list = Array.isArray(members) ? members : [];
      const inactiveMembers = list.filter(isInactiveMember);

      let inActiveFiltered: any[] = [];
      if (filtered.length > 0) {
        inActiveFiltered = filtered.filter(isInactiveMember);
        setFilteredInActive(inActiveFiltered);
      } else {
        setFilteredInActive([]);
      }

      const total = searchActive
        ? inActiveFiltered.length
        : inactiveMembers.length;

      applyTabTotal("2", total);
      setInactive(inactiveMembers);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchLapsed = async () => {
    try {
      const list = Array.isArray(members) ? members : [];
      const lapsedMembers = list.filter(isLapsed);

      let lapseFiltered: any[] = [];
      if (filtered.length > 0) {
        lapseFiltered = filtered.filter(isLapsed);
        setFilteredLapsed(lapseFiltered);
      } else {
        setFilteredLapsed([]);
      }

      const total = searchActive
        ? lapseFiltered.length
        : lapsedMembers.length;

      applyTabTotal("3", total);
      setLapsed(lapsedMembers);
    } catch (err) {
      console.log(err);
    }
  };

  const handleTableChange = (pagination: any) => {
    setStoredTabPage(storagePrefix, state.activeKey, pagination.current);
    setState((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        current: pagination.current,
        pageSize: pagination.pageSize ?? prev.pagination.pageSize,
      },
    }));
  };

  useEffect(() => {
    fetchActive();
    fetchInActive();
    fetchLapsed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, filtered, currentGroup, searchActive]);

  useEffect(() => {
    if (!searchActive) {
      searchRestoreRef.current = true;
      restoreWaitRef.current = false;
      return;
    }

    // One session restore per mount when membersSearch is present; live typing resets to page 1.
    const shouldRestore =
      !didSessionRestoreRef.current &&
      searchRestoreRef.current &&
      Boolean(sessionStorage.getItem("membersSearch"));

    if (shouldRestore && filtered.length === 0 && !restoreWaitRef.current) {
      restoreWaitRef.current = true;
      return;
    }

    const sample = filtered[0];
    let nextKey = getStoredActiveTab(storagePrefix, state.activeKey);
    if (sample) {
      if (isLapsed(sample)) nextKey = "3";
      else if (isInactiveMember(sample)) nextKey = "2";
      else if (isActiveMember(sample)) nextKey = "1";
    }

    let total = 0;
    if (nextKey === "1") {
      total = filtered.filter(isActiveMember).length;
    } else if (nextKey === "2") {
      total = filtered.filter(isInactiveMember).length;
    } else {
      total = filtered.filter(isLapsed).length;
    }

    if (shouldRestore) {
      didSessionRestoreRef.current = true;
      searchRestoreRef.current = false;
      restoreWaitRef.current = false;
      const pageSize = state.pagination.pageSize || 12;
      const current = resolveTabPage(
        storagePrefix,
        nextKey,
        getStoredTabPage(storagePrefix, nextKey),
        total,
        pageSize
      );
      setStoredActiveTab(storagePrefix, nextKey);
      setState((prev) => ({
        ...prev,
        activeKey: nextKey,
        pagination: {
          ...prev.pagination,
          current,
          total,
        },
      }));
      return;
    }

    searchRestoreRef.current = false;
    setStoredActiveTab(storagePrefix, nextKey);
    setStoredTabPage(storagePrefix, nextKey, 1);
    setState((prev) => ({
      ...prev,
      activeKey: nextKey,
      pagination: {
        ...prev.pagination,
        current: 1,
        total,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchActive, filtered]);

  useEffect(() => {
    if (pages.ManageMember) {
      markPageAsVisited("MemberTable");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      title: "ID Number",
      dataIndex: "IdNumber",
      ellipsis: true,
      render: (v: any) => <CopyableCell value={v} />,
    },
    {
      title: "Policy Number",
      dataIndex: ["Policy", "PolicyNumber"],
      ellipsis: true,
      sorter: (a: any, b: any) =>
        a.Policy.PolicyNumber.localeCompare(b.Policy.PolicyNumber),
      render: (v: any) => <CopyableCell value={v} />,
    },
    {
      title: "Name",
      dataIndex: "Name",
      ellipsis: true,
      sorter: (a: any, b: any) => a.Name.localeCompare(b.Name),
    },
    {
      title: "Surname",
      dataIndex: "Surname",
      ellipsis: true,
      sorter: (a: any, b: any) => a.Surname.localeCompare(b.Surname),
    },
    {
      title: "Cellphone",
      dataIndex: "Cellphone",
      ellipsis: true,
    },
    {
      title: "Inception Date",
      dataIndex: "InceptionDate",
      ellipsis: true,
    },
    {
      title: "Policy Premium",
      dataIndex: "TotalPremium",
      ellipsis: true,
    },
    {
      title: "Group",
      dataIndex: ["Group", "GroupName"],
      ellipsis: true,
    },
    {
      title: "",
      dataIndex: "Action",
      width: 180,
      render: (_: any, record: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Button
            ref={refManageMember}
            type="text"
            icon={<SelectOutlined style={{ color: "#ffffff", fontSize: "13px" }} />}
            onClick={() => handleManage(record.id)}
            style={{ padding: "0 4px", color: "#8c8c8c", fontSize: "12px" }}
          >
            Manage Member
          </Button>

          <Dropdown
            overlay={
              <Menu>
                {state.activeKey === "1" && (
                  <>
                    <Menu.Item
                      key="edit"
                      icon={<EditOutlined ref={refEditMember} style={{ color: "blue" }} />}
                      onClick={() => showEditModal(record.id)}
                    >
                      Edit Member
                    </Menu.Item>
                    <Menu.Item
                      key="payment"
                      icon={<CreditCardOutlined ref={refPaymentMethod} style={{ color: "orange" }} />}
                      onClick={() => showPaymentModal(record.id)}
                    >
                      Payment Method
                    </Menu.Item>
                    <Menu.Item
                      key="generate"
                      icon={<FilePdfOutlined ref={refGenerateDocument} style={{ color: "purple" }} />}
                      onClick={() => showDocumentModal(record.id)}
                    >
                      Generate Documents
                    </Menu.Item>
                    <Menu.Item
                      key="send"
                      icon={<SendOutlined ref={refSendDocument} />}
                      onClick={() => showSendModal(record.id)}
                    >
                      Send Documents
                    </Menu.Item>
                    <Menu.Item
                      key="lapse"
                      icon={<ExceptionOutlined ref={refLapsePolicy} style={{ color: "red" }} />}
                      onClick={() => showLapseModal(record.id)}
                    >
                      Lapse Policy
                    </Menu.Item>
                    <Menu.Item
                      key="cancel"
                      icon={<StopOutlined style={{ color: "red" }} />}
                      onClick={() => showCancelModal(record.id)}
                    >
                      Cancel Policy
                    </Menu.Item>
                    <Menu.Item
                      key="premium"
                      icon={<DollarOutlined ref={refLoadPremium} style={{ color: "grey" }} />}
                      onClick={() => showPremiumModal(record.id)}
                    >
                      Load Premium
                    </Menu.Item>
                  </>
                )}
                {state.activeKey === "2" && (
                  <Menu.Item
                    key="activate"
                    disabled={!isValidSACellphone(record.Cellphone)}
                    icon={<FileDoneOutlined style={{ color: "blue" }} />}
                    onClick={() => showActivateModal(record.id)}
                  >
                    {!isValidSACellphone(record.Cellphone) ? (
                      <Tooltip title={ACTIVATE_CELLPHONE_TOOLTIP}>
                        <span style={{ display: "inline-block", width: "100%" }}>
                          Activate Policy
                        </span>
                      </Tooltip>
                    ) : (
                      "Activate Policy"
                    )}
                  </Menu.Item>
                )}
                {state.activeKey === "3" && (
                  <Menu.Item
                    key="activate"
                    disabled={!isValidSACellphone(record.Cellphone)}
                    icon={
                      <FileDoneOutlined
                        ref={refActivatePolicy}
                        style={{ color: "blue" }}
                      />
                    }
                    onClick={() => showActivateModal(record.id)}
                  >
                    {!isValidSACellphone(record.Cellphone) ? (
                      <Tooltip title={ACTIVATE_CELLPHONE_TOOLTIP}>
                        <span style={{ display: "inline-block", width: "100%" }}>
                          Activate Policy
                        </span>
                      </Tooltip>
                    ) : (
                      "Activate Policy"
                    )}
                  </Menu.Item>
                )}
              </Menu>
            }
            trigger={["click"]}
          >
            <MoreOutlined
              rotate={90}
              style={{ cursor: "pointer", fontSize: "16px", color: "#000" }}
            />
          </Dropdown>
        </div>
      ),
    },
  ];

  const lapsedColumns = [
    ...columns.slice(0, -1),
    {
      title: "Policy Status",
      dataIndex: "LapsedReason",
      ellipsis: true,
      render: (reason: string) => reason || "-",
    },
    columns[columns.length - 1],
  ];

  const tabPanes = [
    {
      key: "1",
      title: "Active",
      content: (
        <DataTable
          persistKey="members"
          dataSource={searchActive ? filteredActive : active}
          columns={columns}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
          scroll={false}
        />
      ),
      count: searchActive ? filteredActive.length : active.length,
    },
    {
      key: "2",
      title: "Inactive",
      content: (
        <DataTable
          persistKey="members"
          dataSource={searchActive ? filteredInActive : inactive}
          columns={columns}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
          scroll={false}
        />
      ),
      count: searchActive ? filteredInActive.length : inactive.length,
    },
    {
      key: "3",
      title: "Lapsed",
      content: (
        <DataTable
          persistKey="members"
          dataSource={searchActive ? filteredLapsed : lapsed}
          columns={lapsedColumns}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
          scroll={false}
        />
      ),
      count: searchActive ? filteredLapsed.length : lapsed.length,
    },
  ];

  const visiblePanes = visibleStatusTabs(tabPanes);
  const activeKey = resolveActiveTab(state.activeKey, visiblePanes);

  const changeTab = (key: string) => {
    setStoredActiveTab(storagePrefix, key);
    const storedPage = getStoredTabPage(storagePrefix, key);

    let total = 0;
    if (key === "1") {
      total = searchActive ? filteredActive.length : active.length;
    } else if (key === "2") {
      total = searchActive ? filteredInActive.length : inactive.length;
    } else if (key === "3") {
      total = searchActive ? filteredLapsed.length : lapsed.length;
    }

    const pageSize = state.pagination.pageSize || 12;
    const current = clampPage(storedPage, total, pageSize);
    setStoredTabPage(storagePrefix, key, current);

    setState((prev) => ({
      ...prev,
      activeKey: key,
      loading: false,
      pagination: {
        ...prev.pagination,
        current,
        total,
      },
    }));
  };

  return (
    <>
      <Row>
        <Col span={24}>
          <Tabs
            type="card"
            activeKey={activeKey}
            onChange={changeTab}
            destroyInactiveTabPane
          >
            {visiblePanes.map((pane) => (
              <TabPane
                key={pane.key}
                tab={
                  <>
                    <span>{pane.title}</span>
                    <span
                      style={{
                        color: "#B0B0B0",
                        fontWeight: "300",
                        marginLeft: "3px",
                        fontSize: "12px",
                      }}
                    >
                      ({pane.count})
                    </span>
                  </>
                }
              >
                {pane.content}
              </TabPane>
            ))}
          </Tabs>
        </Col>
      </Row>
    </>
  );
};

export default MemberTable;
